import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { sanitizeToHtml } from "@/components/preview/BlockRenderer";
import styles from "../blocks/blocks.module.scss";

type AsideType = "sidebar" | "pullquote" | "note" | "tip";

const ASIDE_TYPES: AsideType[] = ["sidebar", "pullquote", "note", "tip"];

const ASIDE_LABELS: Record<AsideType, string> = {
  sidebar: "Sidebar",
  pullquote: "Pull Quote",
  note: "Note",
  tip: "Tip",
};

const ASIDE_COLORS: Record<AsideType, string> = {
  sidebar: "#6366f1",
  pullquote: "#8b5cf6",
  note: "#2563eb",
  tip: "#16a34a",
};

export function AsideNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const rawType = node.attrs.asideType as string;
  const asideType: AsideType = ASIDE_TYPES.includes(rawType as AsideType)
    ? (rawType as AsideType)
    : "sidebar";
  const title = (node.attrs.title as string) || "";
  const text = (node.attrs.text as string) || "";

  const color = ASIDE_COLORS[asideType];

  const [isOpen, setIsOpen] = useState(false);
  const [draftType, setDraftType] = useState<AsideType>(asideType);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftText, setDraftText] = useState(text);

  const handleOpen = useCallback(() => {
    setDraftType(asideType);
    setDraftTitle(title);
    setDraftText(text);
    setIsOpen(true);
  }, [asideType, title, text]);

  const handleSave = useCallback(() => {
    updateAttributes({
      asideType: draftType,
      title: draftTitle,
      text: draftText,
    });
    setIsOpen(false);
  }, [draftType, draftTitle, draftText, updateAttributes]);

  const displayTitle = title || ASIDE_LABELS[asideType];

  return (
    <NodeViewWrapper data-type="aside-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.asideBlock}
        style={{ borderLeftColor: color, cursor: "pointer" }}
        onClick={handleOpen}
        title="Click to edit aside"
      >
        <div className={styles.asideHeader}>
          <span className={styles.asideType} style={{ color }}>{ASIDE_LABELS[asideType]}</span>
          {title && <span className={styles.asideTitle}>{title}</span>}
        </div>
        {text ? (
          <div
            className={styles.asideContent}
            dangerouslySetInnerHTML={{ __html: sanitizeToHtml(text) }}
          />
        ) : (
          <div className={styles.asideContent}>
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add aside content...</span>
          </div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title={`Edit ${ASIDE_LABELS[draftType]}`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Type</label>
          <Select value={draftType} onValueChange={(val) => setDraftType((val || "sidebar") as AsideType)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASIDE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{ASIDE_LABELS[type]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Title (optional)</label>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="e.g., Key Concept"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Content</label>
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="Enter aside content..."
            rows={6}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", lineHeight: 1.6,
            }}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
